"use client";

import { motion, useReducedMotion } from "framer-motion";
import { Globe, MessageSquare, Mic, ShieldCheck } from "lucide-react";

const supportingFeatures = [
  {
    icon: MessageSquare,
    title: "Ask for a focused correction",
    description: "Check your wording, grammar, or pronunciation without leaving the conversation.",
  },
  {
    icon: Globe,
    title: "Switch between 70+ languages",
    description: "Use one familiar speaking flow across the languages you are learning.",
  },
  {
    icon: ShieldCheck,
    title: "Rehearse in private",
    description:
      "Try the sentence again before you use it at work, while travelling, or with friends.",
  },
];

export function LandingFeatures() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <section id="features" className="bg-[#F5F5F0] py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={shouldReduceMotion ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          className="mb-10 max-w-3xl md:mb-14"
        >
          <h2 className="text-3xl font-bold tracking-[-0.035em] text-[#1C1C1E] md:text-5xl">
            Speaking practice that meets you where you are
          </h2>
          <p className="mt-4 max-w-[60ch] text-lg leading-8 text-gray-600">
            Choose your language and level, then practise the conversations you want to have.
          </p>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-[1.05fr_1fr] md:gap-10">
          <motion.div
            initial={shouldReduceMotion ? false : { opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            className="flex min-h-[320px] flex-col justify-between rounded-2xl bg-[#FD954F] p-7 text-[#1C1C1E] md:p-10"
          >
            <Mic className="h-10 w-10" aria-hidden="true" />
            <div>
              <h3 className="max-w-[12ch] text-3xl font-bold tracking-[-0.035em] md:text-4xl">
                Talk, then keep talking
              </h3>
              <p className="mt-4 max-w-[42ch] text-lg leading-7 text-[#2C2C2E]">
                The tutor responds to what you say and keeps the exchange at your chosen level.
              </p>
            </div>
          </motion.div>

          <div className="flex flex-col justify-center">
            {supportingFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={shouldReduceMotion ? false : { opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.45 }}
                transition={{ delay: shouldReduceMotion ? 0 : index * 0.08 }}
                className="grid grid-cols-[auto_1fr] gap-4 border-t border-gray-300 py-7 first:border-t-0 md:first:border-t"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-[#A8431F] shadow-sm">
                  <feature.icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1C1C1E]">{feature.title}</h3>
                  <p className="mt-2 max-w-[48ch] leading-7 text-gray-600">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
